import React, { useState, useEffect } from 'react';
import { useBioData } from '../../context/BioDataContext';
import './BenefitStartCalculator.css';

const BenefitStartCalculator = ({ scenario, eligibilityStatus, onDeferredRetirementAgeChange }) => {
  const { bioData } = useBioData();
  const [benefitOptions, setBenefitOptions] = useState([]);
  const [selectedAge, setSelectedAge] = useState(null);

  // Calculate estimated high-3 salary (simplified - in reality this would be more complex)
  const estimateHigh3Salary = () => {
    // This is a simplified calculation - in practice, you'd want more sophisticated logic
    // based on grade progression, locality, etc.
    if (!bioData.currentGrade) return 100000; // Default fallback
    
    // Basic GS pay table approximation (2024 DC locality)
    const gradeMultipliers = {
      12: 0.85, 13: 1.0, 14: 1.18, 15: 1.39
    };
    
    const baseAmount = 85000; // Approximate GS-13 Step 5 in DC
    const multiplier = gradeMultipliers[bioData.currentGrade] || 1.0;
    
    return Math.round(baseAmount * multiplier);
  };

  // Only run this effect once when the component mounts or when eligibility changes
  useEffect(() => {
    if (!eligibilityStatus?.isEligible || !bioData.dateOfBirth) {
      setBenefitOptions([]);
      return;
    }

    const birthYear = new Date(bioData.dateOfBirth).getFullYear();
    const estimatedHigh3 = estimateHigh3Salary();
    const yearsOfService = eligibilityStatus.yearsOfService;
    const basePension = estimatedHigh3 * 0.01 * yearsOfService;

    const options = [];
    
    // Show all eligible ages from the earliest start age up to 62 (full benefit)
    const earliestStart = eligibilityStatus.earliestStartAge;
    
    // Always show all options up to age 62 (full benefit)
    for (let age = earliestStart; age <= 62; age++) {
      let monthlyBenefit = basePension / 12;
      let reductionPercentage = 0;
      let notes = [];

      // Apply age reduction if starting before 62
      if (age < 62) {
        reductionPercentage = (62 - age) * 5;
        monthlyBenefit = monthlyBenefit * (1 - reductionPercentage / 100);
        notes.push(`${reductionPercentage}% reduction for early start`);
      } else if (age === 62) {
        notes.push('Full benefit - no reduction');
      } else {
        notes.push('Full benefit');
      }

      // Calculate start year
      const startYear = birthYear + age;

      options.push({
        age,
        startYear,
        monthlyBenefit: Math.round(monthlyBenefit),
        annualBenefit: Math.round(monthlyBenefit * 12),
        reductionPercentage,
        notes
      });
    }

    setBenefitOptions(options);
    
    // Only set default if no age is currently selected and we have options
    if (!selectedAge && options.length > 0) {
      // If scenario already has a deferred retirement age, use that
      if (scenario?.deferredRetirementAge && options.some(opt => opt.age === scenario.deferredRetirementAge)) {
        setSelectedAge(scenario.deferredRetirementAge);
      } else {
        // Otherwise default to age 62 (full benefit) if available
        const age62Option = options.find(opt => opt.age === 62);
        if (age62Option) {
          setSelectedAge(62);
          // Call the callback to set the default age in the scenario
          if (onDeferredRetirementAgeChange) {
            onDeferredRetirementAgeChange(62);
          }
        } else if (options.length > 0) {
          // If no age 62 option, use the earliest available
          const defaultAge = options[0].age;
          setSelectedAge(defaultAge);
          if (onDeferredRetirementAgeChange) {
            onDeferredRetirementAgeChange(defaultAge);
          }
        }
      }
    }
    // Only run when eligibility status changes, not on every render
  }, [eligibilityStatus, bioData.dateOfBirth, bioData.currentGrade]);

  // We've removed this second useEffect entirely as it was causing infinite updates
  // Instead, we handle scenario.deferredRetirementAge in the main useEffect above

  const handleAgeSelection = (age) => {
    setSelectedAge(age);
    
    // Update the scenario with the selected age
    if (onDeferredRetirementAgeChange) {
      onDeferredRetirementAgeChange(age);
    }
  };

  if (!eligibilityStatus?.isEligible) {
    return null;
  }

  return (
    <div className="benefit-start-calculator">
      <h4>Benefit Start Options</h4>
      <p className="calculator-description">
        Choose when to start your deferred retirement benefits. Your pension is calculated based on your years of service and high-3 salary at separation.
      </p>
      
      <div className="info-box">
        <h5>About Early Retirement Reduction</h5>
        <p>
          FERS deferred benefits are reduced by <strong>5% per year</strong> (5/12% per month) if you begin
          receiving them before age 62. This reduction is <strong>permanent</strong> and will affect your
          pension for your entire retirement.
        </p>
        <p>
          At age 62, you receive your full benefit with no reduction, regardless of your years of service.
        </p>
      </div>

      <div className="benefit-options">
        {benefitOptions && benefitOptions.length > 0 ? benefitOptions.map((option) => (
          <div
            key={option.age}
            className={`benefit-option ${selectedAge === option?.age ? 'selected' : ''} ${option?.age === 62 ? 'recommended' : ''}`}
            onClick={() => handleAgeSelection(option.age)}
          >
            <div className="option-header">
              <div className="age-info">
                <span className="age">Age {option.age}</span>
                <span className="year">({option.startYear})</span>
                {option.age === 62 && <span className="badge">Full Benefit</span>}
                {option.age === eligibilityStatus.earliestStartAge && option.age < 62 && (
                  <span className="badge earliest">Earliest</span>
                )}
              </div>
              <div className="benefit-amount">
                <span className="monthly">${option.monthlyBenefit.toLocaleString()}/month</span>
                <span className="annual">${option.annualBenefit.toLocaleString()}/year</span>
              </div>
            </div>

            {option.reductionPercentage > 0 && (
              <div className="reduction-warning">
                <span className="reduction-text">
                  -{option.reductionPercentage}% reduction ({(option.monthlyBenefit * 100 / (option.monthlyBenefit / (1 - option.reductionPercentage / 100))).toFixed(1)}% of full benefit)
                </span>
              </div>
            )}

            <div className="option-notes">
              {option.notes
                .filter(note => !option.reductionPercentage || !note.includes('reduction'))
                .map((note, index) => (
                  <span key={index} className="note">{note}</span>
                ))}
            </div>
          </div>
        )) : <p>No eligible benefit start options found. Please check your service history and eligibility.</p>}
      </div>

      {selectedAge && benefitOptions && benefitOptions.length > 0 && (
        <div className="selection-summary">
          <h5>Selected Option Summary</h5>
          <div className="summary-details">
            {(() => {
              const selected = benefitOptions.find(opt => opt.age === selectedAge);
              if (!selected) return <p>Selected option information not available</p>;
              
              return (
                <>
                  <p><strong>Start Age:</strong> {selected.age} (Year {selected.startYear})</p>
                  <p><strong>Monthly Benefit:</strong> ${selected.monthlyBenefit.toLocaleString()}</p>
                  <p><strong>Annual Benefit:</strong> ${selected.annualBenefit.toLocaleString()}</p>
                  {selected.reductionPercentage > 0 && (
                    <>
                      <p className="warning-text">
                        <strong>Reduction:</strong> {selected.reductionPercentage}% below full benefit
                      </p>
                      <p className="warning-text">
                        <strong>Full benefit at age 62:</strong> ${Math.round(selected.monthlyBenefit / (1 - selected.reductionPercentage / 100)).toLocaleString()}/month
                      </p>
                      <p className="note-text">
                        This reduction is permanent and will continue for your lifetime.
                      </p>
                    </>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      <div className="calculator-footnote">
        <small>
          * Estimates based on current salary and service data. Actual benefits may vary based on 
          final high-3 salary, exact service computation, and cost-of-living adjustments.
        </small>
      </div>
    </div>
  );
};

export default BenefitStartCalculator;
